/**
 * Localizes a support role using the i18n key dictionary defined in
 * `i18n/translations.js`. Each role exposes:
 *   - title     : `role.role` (job title, e.g. "Comptable")
 *   - background: `role.background` (one-line bio)
 *   - prereq    : `role.prerequisite.label` (unlock condition)
 *   - bonuses[] : array of `{ ...originalBonus, label }` where label is
 *                 the localized human-readable description.
 *
 * Key convention :
 *   support_role_<id_with_underscores>_title
 *   support_role_<id_with_underscores>_bg
 *   support_role_<id_with_underscores>_prereq
 *   support_role_<id_with_underscores>_bonus_<index>
 *
 * If a key is missing, falls back to the FR source on the role itself so
 * consumers never see empty strings.
 */
const idToKey = (id) => String(id || '').replace(/-/g, '_');

const tryT = (t, key, fallback) => {
  if (typeof t !== 'function' || !key) return fallback;
  const v = t(key);
  // useTranslation falls back to the key itself when missing — detect it.
  return (v && v !== key) ? v : fallback;
};

export const getLocalizedSupportRole = (role, t) => {
  if (!role) return role;
  const base = idToKey(role.id);
  const title = tryT(t, `support_role_${base}_title`, role.role);
  const background = tryT(t, `support_role_${base}_bg`, role.background);
  const prereqLabel = role.prerequisite
    ? tryT(t, `support_role_${base}_prereq`, role.prerequisite.label)
    : null;
  const bonuses = (role.bonuses || []).map((b, idx) => ({
    ...b,
    label: tryT(t, `support_role_${base}_bonus_${idx}`, b.label),
  }));
  const out = {
    ...role,
    role: title,
    background,
    bonuses,
  };
  if (role.prerequisite) {
    out.prerequisite = { ...role.prerequisite, label: prereqLabel };
  }
  return out;
};

export const getLocalizedSupportFamily = (family, t) => {
  if (!family) return family;
  const v = tryT(t, `support_family_${family.id}_name`, family.name);
  return { ...family, name: v };
};

const TOOL_PREFIX = 'support_tool_';
export const getLocalizedSupportTool = (tool, t) => {
  if (!tool) return tool;
  const base = idToKey(tool.id);
  const name = tryT(t, `${TOOL_PREFIX}${base}_name`, tool.name);
  const description = tryT(t, `${TOOL_PREFIX}${base}_desc`, tool.description);
  const category = tryT(t, `${TOOL_PREFIX}${base}_category`, tool.category);
  const condition = tryT(t, `${TOOL_PREFIX}${base}_condition`, tool.condition);
  return { ...tool, name, description, category, condition };
};
